import { logger, generateRequestId } from '../utils/logger';
import offlineStorageService from './offlineStorageService';
import symptomService from './symptomService';

/**
 * Offline Sync Service
 * Monitors connectivity and automatically syncs pending check-ins
 * Provides real-time sync status updates
 */

class OfflineSyncService {
  constructor() {
    this.isOnline = navigator.onLine;
    this.isSyncing = false;
    this.syncListeners = [];
    this.statusListeners = [];
    this.autoSyncInterval = null;
    
    // Setup connectivity listeners
    this.setupConnectivityListeners();
    
    // Start auto-sync check every 30 seconds when online
    this.startAutoSync();
  }

  /**
   * Setup online/offline event listeners
   */
  setupConnectivityListeners() {
    window.addEventListener('online', () => {
      logger?.debug('Connection restored - triggering sync');
      this.isOnline = true;
      this.notifyStatusChange({ online: true });
      this.syncPendingCheckIns();
    });

    window.addEventListener('offline', () => {
      logger?.debug('Connection lost - offline mode activated');
      this.isOnline = false;
      this.notifyStatusChange({ online: false });
    });
  }

  /**
   * Start automatic sync check
   */
  startAutoSync() {
    // Check every 30 seconds if online
    this.autoSyncInterval = setInterval(() => {
      if (this.isOnline && !this.isSyncing) {
        this.syncPendingCheckIns();
      }
    }, 30000);
  }

  /**
   * Stop automatic sync
   */
  stopAutoSync() {
    if (this.autoSyncInterval) {
      clearInterval(this.autoSyncInterval);
      this.autoSyncInterval = null;
    }
  }

  /**
   * Check if device is online
   * @returns {boolean}
   */
  getOnlineStatus() {
    return this.isOnline;
  }

  /**
   * Subscribe to sync events
   * @param {Function} callback - Callback function
   * @returns {Function} - Unsubscribe function
   */
  onSyncEvent(callback) {
    this.syncListeners?.push(callback);
    return () => {
      this.syncListeners = this.syncListeners?.filter(cb => cb !== callback);
    };
  }

  /**
   * Subscribe to status changes
   * @param {Function} callback - Callback function
   * @returns {Function} - Unsubscribe function
   */
  onStatusChange(callback) {
    this.statusListeners?.push(callback);
    return () => {
      this.statusListeners = this.statusListeners?.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify sync event listeners
   * @param {object} event - Sync event data
   */
  notifySyncEvent(event) {
    this.syncListeners?.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        logger?.error('Error in sync event listener', error);
      }
    });
  }

  /**
   * Notify status change listeners
   * @param {object} status - Status data
   */
  notifyStatusChange(status) {
    this.statusListeners?.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        logger?.error('Error in status change listener', error);
      }
    });
  }

  /**
   * Sync all pending check-ins
   * @param {string} userId - User ID
   * @returns {Promise<{success: boolean, synced: number, failed: number}>}
   */
  async syncPendingCheckIns(userId = null) {
    if (!this.isOnline) {
      logger?.debug('Cannot sync - device is offline');
      return { success: false, synced: 0, failed: 0, reason: 'offline' };
    }

    if (this.isSyncing) {
      logger?.debug('Sync already in progress');
      return { success: false, synced: 0, failed: 0, reason: 'already_syncing' };
    }

    const requestId = generateRequestId();
    this.isSyncing = true;
    
    this.notifySyncEvent({ 
      type: 'sync_started', 
      timestamp: new Date()?.toISOString() 
    });

    try {
      // Get pending check-ins
      const pendingCheckIns = userId 
        ? await offlineStorageService?.getPendingCheckIns(userId)
        : await this.getAllPendingCheckIns();

      if (pendingCheckIns?.length === 0) {
        logger?.debug('No pending check-ins to sync');
        this.isSyncing = false;
        return { success: true, synced: 0, failed: 0 };
      }

      logger?.debug('Starting sync', { requestId, count: pendingCheckIns?.length });

      let syncedCount = 0;
      let failedCount = 0;

      // Sync each check-in
      for (const checkIn of pendingCheckIns) {
        try {
          // Prepare data for symptom service
          const entryData = {
            feelingLevel: checkIn?.feelingLevel,
            symptomCategories: checkIn?.symptomCategories || [],
            bodyLocations: checkIn?.bodyLocations || [],
            severity: checkIn?.severity || 5,
            timing: checkIn?.timing || '',
            triggers: checkIn?.triggers || [],
            dailyImpacts: checkIn?.dailyImpacts || [],
            medicationStatus: checkIn?.medicationStatus || '',
            medicationDetails: checkIn?.medicationDetails || '',
            notes: checkIn?.notes || '',
            encryptionPassword: checkIn?.encryptionPassword || null
          };

          // Attempt to save to database
          const { data, error } = await symptomService?.createEntry(entryData);

          if (error) {
            logger?.error('Failed to sync check-in', error, { requestId, checkInId: checkIn?.id });
            await offlineStorageService?.markAsFailed(checkIn?.id, error?.message);
            failedCount++;
            
            this.notifySyncEvent({
              type: 'sync_item_failed',
              checkInId: checkIn?.id,
              error: error?.message,
              timestamp: new Date()?.toISOString()
            });
          } else {
            logger?.debug('Check-in synced successfully', { requestId, checkInId: checkIn?.id, entryId: data?.id });
            await offlineStorageService?.markAsSynced(checkIn?.id);
            syncedCount++;
            
            this.notifySyncEvent({
              type: 'sync_item_success',
              checkInId: checkIn?.id,
              entryId: data?.id,
              timestamp: new Date()?.toISOString()
            });
          }
        } catch (error) {
          logger?.error('Unexpected error syncing check-in', error, { requestId, checkInId: checkIn?.id });
          await offlineStorageService?.markAsFailed(checkIn?.id, error?.message);
          failedCount++;
          
          this.notifySyncEvent({
            type: 'sync_item_failed',
            checkInId: checkIn?.id,
            error: error?.message,
            timestamp: new Date()?.toISOString()
          });
        }
      }

      logger?.audit('OFFLINE_SYNC_COMPLETED', userId, { 
        requestId, 
        synced: syncedCount, 
        failed: failedCount 
      });

      this.notifySyncEvent({
        type: 'sync_completed',
        synced: syncedCount,
        failed: failedCount,
        timestamp: new Date()?.toISOString()
      });

      // Cleanup old synced entries
      await offlineStorageService?.cleanupSyncedEntries(7);

      return { success: true, synced: syncedCount, failed: failedCount };
    } catch (error) {
      logger?.error('Sync process error', error, { requestId });
      
      this.notifySyncEvent({
        type: 'sync_error',
        error: error?.message,
        timestamp: new Date()?.toISOString()
      });
      
      return { success: false, synced: 0, failed: 0, error: error?.message };
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Get all pending check-ins across all users (for admin)
   * @returns {Promise<Array>}
   */
  async getAllPendingCheckIns() {
    try {
      const allEntries = await offlineStorageService?.getAllEntries();
      return allEntries?.filter(entry => entry?.syncStatus === 'pending');
    } catch (error) {
      logger?.error('Error getting all pending check-ins', error);
      return [];
    }
  }

  /**
   * Get pending count for user
   * @param {string} userId - User ID
   * @returns {Promise<number>}
   */
  async getPendingCount(userId) {
    return await offlineStorageService?.getPendingCount(userId);
  }

  /**
   * Manually trigger sync
   * @param {string} userId - User ID
   * @returns {Promise<object>}
   */
  async manualSync(userId) {
    logger?.debug('Manual sync triggered', { userId });
    return await this.syncPendingCheckIns(userId);
  }

  /**
   * Get sync status
   * @returns {object}
   */
  getSyncStatus() {
    return {
      online: this.isOnline,
      syncing: this.isSyncing
    };
  }
}

// Export singleton instance
const offlineSyncService = new OfflineSyncService();
export default offlineSyncService;